import React, { useState, useEffect } from "react";
import { X, FileText } from "lucide-react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { useSelector, useDispatch } from "react-redux";
import { postRequest } from "../../services";
import { createObservationEvaluation } from "../../redux/observationEvaluationSlice";
import { parseDomainJson, RATING_LABELS } from "../../utils/domainEvaluationData";

const AdminObservationEvaluationModal = ({
  isOpen,
  onClose,
  teacherEmail,
  campusClasses,
  onEvaluationAdded,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { register, handleSubmit, reset, watch, setValue, trigger } = useForm();
  const [currentSection, setCurrentSection] = useState(1);
  const [domains, setDomains] = useState([]);
  const [domainLoading, setDomainLoading] = useState(true);
  const [domainResponses, setDomainResponses] = useState({ 1: {}, 2: {}, 3: {}, 4: {} });
  const [attachments, setAttachments] = useState([]);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});

  const { loading: isSubmitting, error } = useSelector((state) => state.observationEvaluation);
  const user = JSON.parse(localStorage.getItem("user"));
  const observerName =
    user?.firstName && user?.lastName ? `${user.firstName} ${user.lastName}` : user?.email || "";
  const teachers = useSelector((state) => state.teachers.teachers || []);

  const uniqueClasses =
    campusClasses?.flatMap((campus) =>
      campus.classes?.map((cls) => ({ id: cls.id, name: cls.className, campusId: campus.id }))
    ) || [];
  const allSubjects =
    campusClasses?.flatMap((campus) =>
      campus.classes?.flatMap((cls) =>
        cls.sections?.flatMap((section) =>
          section.subjects?.map((subject) => ({
            id: subject.id,
            name: subject.subjectName,
            classId: cls.id,
          }))
        )
      )
    ) || [];
  const uniqueTeachers =
    teachers?.map((tchr) => ({
      email: tchr.teacher?.email,
      name: `${tchr.teacher?.firstName || ""} ${tchr.teacher?.lastName || ""}`.trim(),
    })).filter((tchr) => tchr.name) || [];

  const selectedClassId = watch("classGrade");
  const selectedCampusId = watch("campusId");
  const selectedSubjectId = watch("subject");
  const filteredClasses = selectedCampusId
    ? uniqueClasses.filter((cls) => cls.campusId === selectedCampusId)
    : uniqueClasses;
  const filteredSubjects = selectedClassId
    ? allSubjects.filter((s) => s.classId === selectedClassId)
    : allSubjects;
  
  // Filter teachers based on selected subject
  const filteredTeachers = selectedSubjectId
    ? uniqueTeachers.filter((tchr) => {
        const teacher = teachers.find((t) => t.teacher?.email === tchr.email);
        if (!teacher?.assignedClasses) return false;
        // Check if teacher teaches the selected subject
        return teacher.assignedClasses.some((classItem) =>
          classItem.subjects?.some((subject) => subject.subjectId === selectedSubjectId)
        );
      })
    : uniqueTeachers;

  useEffect(() => {
    if (isOpen) {
      setValue("observerName", observerName);
      setValue("teacherEmail", teacherEmail);
    }
  }, [isOpen, observerName, teacherEmail, setValue]);

  useEffect(() => {
    if (!isOpen) {
      reset();
      setCurrentSection(1);
      setDomainResponses({ 1: {}, 2: {}, 3: {}, 4: {} });
      setAttachments([]);
      setUploadProgress({});
    }
  }, [isOpen, reset]);

  useEffect(() => {
    let cancelled = false;
    setDomainLoading(true);
    fetch(`${process.env.PUBLIC_URL || ""}/domain.json`)
      .then((res) => res.json())
      .then((arr) => {
        if (!cancelled) {
          const parsed = parseDomainJson(arr);
          setDomains(parsed || []);
        }
      })
      .catch(() => {
        if (!cancelled) setDomains([]);
      })
      .finally(() => {
        if (!cancelled) setDomainLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (selectedClassId) {
      setValue("subject", "");
      setValue("teacherName", ""); // Reset teacher when class changes
    }
  }, [selectedClassId, setValue]);

  useEffect(() => {
    if (selectedSubjectId) {
      setValue("teacherName", ""); // Reset teacher when subject changes
    }
  }, [selectedSubjectId, setValue]);

  useEffect(() => {
    if (error) {
      toast.error(error || t("Failed To Add Observation Evaluation") || "Failed to add observation evaluation");
    }
  }, [error, t]);

  const setDomainResponse = (domainIndex, questionKey, field, value) => {
    setDomainResponses((prev) => ({
      ...prev,
      [domainIndex]: {
        ...(prev[domainIndex] || {}),
        [questionKey]: {
          ...(prev[domainIndex]?.[questionKey] || {}),
          [field]: value,
        },
      },
    }));
  };

  const getDomainResponse = (domainIndex, questionKey, field) => {
    return domainResponses[domainIndex]?.[questionKey]?.[field] ?? "";
  };

  const handleFileChange = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    setUploadingFiles(true);
    const newAttachments = [...attachments];
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const fileIndex = attachments.length + i;
      setUploadProgress((prev) => ({ ...prev, [fileIndex]: 0 }));
      try {
        const formData = new FormData();
        formData.append("files", file);
        const progressInterval = setInterval(() => {
          setUploadProgress((prev) => {
            const current = prev[fileIndex] || 0;
            return current < 90 ? { ...prev, [fileIndex]: current + 10 } : prev;
          });
        }, 200);
        const uploadResponse = await postRequest("/api/Document/documents-upload", formData, true);
        clearInterval(progressInterval);
        setUploadProgress((prev) => ({ ...prev, [fileIndex]: 100 }));
        if (uploadResponse?.data?.[0]) {
          newAttachments.push({ file, url: uploadResponse.data[0], name: file.name });
        }
        setTimeout(() => {
          setUploadProgress((prev) => {
            const next = { ...prev };
            delete next[fileIndex];
            return next;
          });
        }, 500);
      } catch (err) {
        console.error("Error uploading file:", err);
        toast.error(`Failed to upload ${file.name}`);
        setUploadProgress((prev) => {
          const next = { ...prev };
          delete next[fileIndex];
          return next;
        });
      }
    }
    setAttachments(newAttachments);
    setUploadingFiles(false);
    e.target.value = "";
  };

  const removeAttachment = (index) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
    setUploadProgress((prev) => {
      const next = { ...prev };
      delete next[index];
      return next;
    });
  };

  const validateStep1 = async () => {
    const valid = await trigger([
      "observationDate",
      "observerName",
      "classGrade",
      "subject",
      "teacherName",
      "lessonTopic",
    ]);
    if (!valid) toast.error(t("Please fill all required fields") || "Please fill all required fields in this section");
    return valid;
  };

  const validateDomainStep = (domainIndex) => {
    const domain = domains[domainIndex];
    if (!domain || !domain.questions) return true;
    const questionEntries = Object.entries(domain.questions).filter(
      ([k, v]) => !k.startsWith("_") && v && typeof v === "object" && "Below Standard" in v
    );
    for (const [questionKey] of questionEntries) {
      const rating = getDomainResponse(domainIndex + 1, questionKey, "rating");
      if (!rating) {
        toast.error(t("Please select a rating for each question") || "Please select a rating for each question");
        return false;
      }
    }
    return true;
  };

  const validateStep6 = async () => {
    const valid = await trigger([
      "strengthsObserved",
      "areasForImprovement",
      "actionPlan",
      "overallRating",
      "overallRatingDetails",
    ]);
    if (!valid) toast.error(t("Please fill all required fields") || "Please fill all required fields in this section");
    return valid;
  };

  const validateStep7 = () => {
    if (!attachments || attachments.length === 0) {
      toast.error(t("Please upload at least one document") || "Please upload at least one document in step 7");
      return false;
    }
    const allUploaded = attachments.every((att) => att.url);
    if (!allUploaded) {
      toast.error(t("Please wait for all files to finish uploading") || "Please wait for all files to finish uploading");
      return false;
    }
    return true;
  };

  const nextSection = async () => {
    if (currentSection === 1) {
      const ok = await validateStep1();
      if (!ok) return;
    } else if (currentSection >= 2 && currentSection <= 5) {
      const ok = validateDomainStep(currentSection - 2);
      if (!ok) return;
    } else if (currentSection === 6) {
      const ok = await validateStep6();
      if (!ok) return;
    } else if (currentSection === 7) {
      const ok = validateStep7();
      if (!ok) return;
    }
    if (currentSection < 7) setCurrentSection((s) => s + 1);
  };

  const prevSection = () => {
    if (currentSection > 1) setCurrentSection((s) => s - 1);
  };

  const buildDomainPayload = (domainIndex) => {
    const domain = domains[domainIndex];
    if (!domain) return { domainTitle: "", responses: [] };
    const questionEntries = Object.entries(domain.questions || {}).filter(
      ([k, v]) => !k.startsWith("_") && v && typeof v === "object" && "Below Standard" in v
    );
    const responses = questionEntries.map(([questionKey]) => ({
      questionKey,
      selectedRating: getDomainResponse(domainIndex + 1, questionKey, "rating") || "Developing",
      comment: getDomainResponse(domainIndex + 1, questionKey, "comment") || null,
    }));
    return { domainTitle: domain.title, responses };
  };

  const onSubmit = async (data) => {
    const step1Valid = await validateStep1();
    if (!step1Valid) return;
    for (let i = 0; i < 4; i++) {
      if (!validateDomainStep(i)) return;
    }
    const step6Valid = await validateStep6();
    if (!step6Valid) return;
    const step7Valid = validateStep7();
    if (!step7Valid) return;
    try {
      const attachmentUrls = [];
      if (attachments.length > 0) {
        for (const attachment of attachments) {
          if (attachment.url) {
            attachmentUrls.push(attachment.url);
          } else if (attachment.file) {
            setUploadingFiles(true);
            try {
              const formData = new FormData();
              formData.append("files", attachment.file);
              const uploadResponse = await postRequest("/api/Document/documents-upload", formData, true);
              if (uploadResponse?.data?.[0]) attachmentUrls.push(uploadResponse.data[0]);
            } catch (err) {
              console.error("Error uploading file:", err);
              toast.error(`Failed to upload ${attachment.name || attachment.file?.name}`);
            } finally {
              setUploadingFiles(false);
            }
          }
        }
      }
      const payload = {
        teacherEmail: data.teacherEmail || teacherEmail,
        basicInformation: {
          observationDate: data.observationDate,
          observerName: data.observerName,
          observerDesignation: data.observerDesignation || user?.designation || "",
          classGrade: data.classGrade,
          subject: data.subject,
          teacherName: data.teacherName,
          lessonTopic: data.lessonTopic,
        },
        domain1: buildDomainPayload(0),
        domain2: buildDomainPayload(1),
        domain3: buildDomainPayload(2),
        domain4: buildDomainPayload(3),
        principalsRemarks: {
          strengthsObserved: data.strengthsObserved,
          areasForImprovement: data.areasForImprovement,
          actionPlan: data.actionPlan,
          followUpDate: data.followUpDate || null,
          overallRating: data.overallRating,
          overallRatingDetails: data.overallRatingDetails,
        },
        attachments: attachmentUrls,
      };
      const result = await dispatch(createObservationEvaluation(payload)).unwrap();
      if (result) {
        toast.success(t("Observation Evaluation Added Successfully") || "Observation evaluation added successfully");
        reset();
        setCurrentSection(1);
        setDomainResponses({ 1: {}, 2: {}, 3: {}, 4: {} });
        setAttachments([]);
        setUploadProgress({});
        if (onEvaluationAdded) onEvaluationAdded();
        if (onClose) onClose();
      }
    } catch (err) {
      console.error(err);
    }
  };

  if (!isOpen) return null;

  const renderStep1 = () => (
    <div key="step1" className="space-y-4">
      <h3 className="text-xl font-bold text-blue-500 mb-4">
        {t("Basic Information") || "1. Basic Information"}
      </h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block mb-2 font-medium">{t("Observation Date") || "Observation Date"} *</label>
          <input type="date" {...register("observationDate", { required: true })} className="w-full p-2 border rounded" />
        </div>
        <div>
          <label className="block mb-2 font-medium">{t("Observer Name") || "Observer Name / Designation"} *</label>
          <input type="text" {...register("observerName", { required: true })} className="w-full p-2 border rounded" defaultValue={observerName} />
        </div>
        <div>
          <label className="block mb-2 font-medium">{t("Observer Designation") || "Observer Designation"}</label>
          <input type="text" {...register("observerDesignation")} className="w-full p-2 border rounded" defaultValue={user?.designation || ""} />
        </div>
        <div>
          <label className="block mb-2 font-medium">{t("Campus") || "Campus"}</label>
          <select {...register("campusId")} className="w-full p-2 border rounded">
            <option value="">{t("Select Campus") || "Select Campus"}</option>
            {campusClasses?.map((campus) => (
              <option key={campus.id} value={campus.id}>{campus.name}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block mb-2 font-medium">{t("Class Grade") || "Class / Grade"} *</label>
          <select {...register("classGrade", { required: true })} className="w-full p-2 border rounded">
            <option value="">{t("Select Class") || "Select Class"}</option>
            {filteredClasses.map((cls) => (
              <option key={cls.id} value={cls.id}>{cls.name}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block mb-2 font-medium">{t("Subject") || "Subject"} *</label>
          <select {...register("subject", { required: true })} className="w-full p-2 border rounded" disabled={!selectedClassId}>
            <option value="">{selectedClassId ? (t("Select Subject") || "Select Subject") : (t("Select Class First") || "Select Class First")}</option>
            {filteredSubjects.map((subject) => (
              <option key={subject.id} value={subject.id}>{subject.name}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block mb-2 font-medium">{t("Teacher Name") || "Teacher Name"} *</label>
          <select 
            {...register("teacherName", { required: true })} 
            className="w-full p-2 border rounded"
            disabled={!selectedSubjectId}
          >
            <option value="">
              {selectedSubjectId 
                ? (t("Select Teacher") || "Select Teacher") 
                : (t("Select Subject First") || "Select Subject First")}
            </option>
            {filteredTeachers.map((tchr, idx) => (
              <option key={idx} value={tchr.email}>{tchr.name}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block mb-2 font-medium">{t("Lesson Topic") || "Lesson Topic"} *</label>
          <input type="text" {...register("lessonTopic", { required: true })} className="w-full p-2 border rounded" placeholder={t("Enter Lesson Topic") || "Enter lesson topic"} />
        </div>
      </div>
    </div>
  );

  const renderDomainStep = (domainIndex) => {
    const domain = domains[domainIndex];
    if (!domain || !domain.questions) return null;
    const questionEntries = Object.entries(domain.questions).filter(
      ([k, v]) => !k.startsWith("_") && v && typeof v === "object" && "Below Standard" in v
    );
    // Apply specific colors to each domain title
    const domainColors = [
      "text-[#217223]", // Domain 1: Dark green
      "text-[#f7b500]", // Domain 2: Orange-yellow
      "text-[#5B2C84]", // Domain 3: Deep purple
      "text-[#ff6b35]", // Domain 4: Orange
    ];
    const titleColor = domainColors[domainIndex] || "text-blue-500";
    return (
      <div key={`domain-${domainIndex}`} className="space-y-6">
        <h3 className={`text-xl font-bold ${titleColor} mb-4`}>{domain.title}</h3>
        {questionEntries.map(([questionKey, criteria]) => (
          <div key={questionKey} className="border border-gray-200 rounded-lg p-4 bg-gray-50/50">
            <h4 className="font-semibold text-gray-800 mb-3">{questionKey}</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 mb-3">
              {RATING_LABELS.map((label) => {
                const text = criteria[label] || "";
                const name = `d${domainIndex + 1}_${questionKey}_rating`;
                const isChecked = getDomainResponse(domainIndex + 1, questionKey, "rating") === label;
                return (
                  <div
                    key={label}
                    className={`border rounded-lg p-3 transition-colors ${isChecked ? "ring-2 ring-blue-500 border-blue-500 bg-blue-50/50" : "border-gray-200 bg-white"}`}
                  >
                    <label className="block cursor-pointer">
                      <div className="flex items-start gap-2">
                        <input
                          type="radio"
                          name={name}
                          checked={isChecked}
                          onChange={() => setDomainResponse(domainIndex + 1, questionKey, "rating", label)}
                          className="mt-1"
                        />
                        <div>
                          <span className="font-medium text-gray-700 block text-sm">{label.toUpperCase()}</span>
                          <p className="text-xs text-gray-600 mt-1">{text}</p>
                        </div>
                      </div>
                    </label>
                  </div>
                );
              })}
            </div>
            <div className="mt-3">
              <label className="block text-sm font-medium text-gray-700 mb-1">{t("Comments") || "Comments"}</label>
              <textarea
                value={getDomainResponse(domainIndex + 1, questionKey, "comment")}
                onChange={(e) => setDomainResponse(domainIndex + 1, questionKey, "comment", e.target.value)}
                rows={2}
                className="w-full p-2 border rounded text-sm"
                placeholder={t("Admin comments (optional)") || "Admin comments (optional)"}
              />
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderStep6 = () => (
    <div key="step6" className="space-y-4">
      <h3 className="text-xl font-bold text-blue-500 mb-4">
        {t("Principals Remarks") || "6. Principal's Remarks"}
      </h3>
      <div className="space-y-4">
        <div>
          <label className="block mb-2 font-medium">{t("Strengths Observed") || "Strengths Observed"} *</label>
          <textarea
            {...register("strengthsObserved", { required: true })}
            rows={4}
            className="w-full p-2 border rounded"
            placeholder={t("Describe Strengths") || "Describe strengths observed"}
          />
        </div>
        <div>
          <label className="block mb-2 font-medium">{t("Areas For Improvement") || "Areas for Improvement"} *</label>
          <textarea
            {...register("areasForImprovement", { required: true })}
            rows={4}
            className="w-full p-2 border rounded"
            placeholder={t("Describe Areas For Improvement") || "Describe areas for improvement"}
          />
        </div>
        <div>
          <label className="block mb-2 font-medium">{t("Action Plan Recommendations") || "Action Plan / Recommendations"} *</label>
          <textarea
            {...register("actionPlan", { required: true })}
            rows={4}
            className="w-full p-2 border rounded"
            placeholder={t("Describe Action Plan") || "Describe action plan and recommendations"}
          />
        </div>
        <div>
          <label className="block mb-2 font-medium">{t("Follow Up Date") || "Follow-up Date (if any)"}</label>
          <input type="date" {...register("followUpDate")} className="w-full p-2 border rounded" />
        </div>
        <div>
          <label className="block mb-2 font-medium">{t("Overall Rating Score") || "Overall Rating / Score"} *</label>
          <select
            {...register("overallRating", { required: true })}
            className="w-full p-2 border rounded mb-2"
            autoComplete="new-password"
            data-lpignore="true"
          >
            <option value="">{t("Select Rating") || "Select Rating"}</option>
            <option value="Below Standard">{t("Below Standard") || "Below Standard"}</option>
            <option value="Developing">{t("Developing") || "Developing"}</option>
            <option value="Proficient">{t("Proficient") || "Proficient"}</option>
            <option value="Exemplary">{t("Exemplary") || "Exemplary"}</option>
          </select>
          <textarea
            {...register("overallRatingDetails", { required: true })}
            rows={2}
            className="w-full p-2 border rounded"
            placeholder={t("Provide Rating Details") || "Provide rating details"}
          />
        </div>
      </div>
    </div>
  );

  const renderStep7 = () => (
    <div key="step7" className="space-y-4">
      <h3 className="text-xl font-bold text-blue-500 mb-4">
        {t("Attachments Evidence") || "7. Attachments / Evidence"}
      </h3>
      <div className="space-y-4">
        <div>
          <label className="block mb-2 font-medium">
            {t("Upload Photos Lesson Plans") || "Upload Photos / Lesson Plans / Student Work Samples"}
          </label>
          <div className="relative">
            <input
              type="file"
              multiple
              onChange={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleFileChange(e);
              }}
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => e.key === "Enter" && e.preventDefault()}
              accept="image/*,.pdf,.doc,.docx"
              className="w-full p-2 border rounded"
              disabled={uploadingFiles}
              formNoValidate
            />
            {uploadingFiles && (
              <div className="absolute inset-0 bg-blue-50 bg-opacity-75 flex items-center justify-center rounded">
                <div className="flex flex-col items-center gap-2">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500" />
                  <span className="text-xs text-blue-600 font-medium">{t("Uploading Files") || "Uploading files..."}</span>
                </div>
              </div>
            )}
          </div>
          <p className="text-sm text-gray-500 mt-1">{t("You Can Upload Multiple Files") || "You can upload multiple files"}</p>
        </div>
        {attachments.length > 0 && (
          <div className="mt-4">
            <h4 className="font-medium mb-2">{t("Uploaded Files") || "Uploaded Files"}:</h4>
            <ul className="space-y-2">
              {attachments.map((attachment, index) => (
                <li
                  key={index}
                  className="flex items-center justify-between p-3 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100"
                >
                  <div className="flex-1 flex items-center gap-3">
                    <FileText size={16} className="text-blue-500" />
                    <span className="text-sm font-medium text-gray-700">
                      {attachment.name || attachment.file?.name || `File ${index + 1}`}
                    </span>
                    {attachment.url && (
                      <span className="text-xs text-green-600 bg-green-50 px-2 py-1 rounded">{t("Uploaded") || "Uploaded"}</span>
                    )}
                    {uploadProgress[index] !== undefined && (
                      <div className="flex-1 max-w-xs">
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div className="bg-blue-500 h-2 rounded-full transition-all" style={{ width: `${uploadProgress[index]}%` }} />
                        </div>
                        <span className="text-xs text-gray-600">{uploadProgress[index]}%</span>
                      </div>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => removeAttachment(index)}
                    className="text-red-500 hover:text-red-700 p-1 hover:bg-red-50 rounded disabled:opacity-50"
                    disabled={uploadingFiles}
                  >
                    <X size={16} />
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );

  const renderSection = () => {
    if (currentSection === 1) return renderStep1();
    if (currentSection >= 2 && currentSection <= 5 && domains.length >= currentSection - 1) {
      return renderDomainStep(currentSection - 2);
    }
    if (currentSection === 6) return renderStep6();
    if (currentSection === 7) return renderStep7();
    if (domainLoading) {
      return (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500" />
        </div>
      );
    }
    return null;
  };

  const totalSteps = 7;
  const isLastStep = currentSection === totalSteps;

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
      <div className="bg-white rounded-lg shadow-lg w-[90%] max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-2xl font-bold text-blue-500">
            {t("Observation Evaluation") || "Observation Evaluation"}
          </h2>
          <button type="button" onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <X size={24} />
          </button>
        </div>
        <form onSubmit={handleSubmit(onSubmit)} className="flex-1 overflow-y-auto" autoComplete="off" noValidate>
          <div className="p-6">
            <div className="mb-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium">{t("Step") || "Step"} {currentSection} {t("of") || "of"} {totalSteps}</span>
                <span className="text-sm text-gray-500">{Math.round((currentSection / totalSteps) * 100)}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div className="bg-blue-500 h-2 rounded-full transition-all duration-300" style={{ width: `${(currentSection / totalSteps) * 100}%` }} />
              </div>
            </div>
            {renderSection()}
          </div>
          <div className="flex items-center justify-between p-4 border-t bg-gray-50">
            <button
              type="button"
              onClick={prevSection}
              disabled={currentSection === 1}
              className={`px-4 py-2 rounded ${currentSection === 1 ? "bg-gray-300 text-gray-500 cursor-not-allowed" : "bg-gray-500 text-white hover:bg-gray-600"}`}
            >
              {t("Previous") || "Previous"}
            </button>
            <div className="flex gap-2">
              {!isLastStep ? (
                <button
                  type="button"
                  onClick={nextSection}
                  disabled={domainLoading || uploadingFiles}
                  className="px-6 py-2 rounded font-medium bg-blue-500 text-white hover:bg-blue-600 disabled:opacity-50"
                >
                  {t("Next") || "Next"}
                </button>
              ) : (
                <button
                  type="submit"
                  disabled={isSubmitting || domainLoading || uploadingFiles}
                  className={`px-6 py-2 rounded font-medium ${isSubmitting || domainLoading || uploadingFiles ? "bg-gray-400 text-gray-600 cursor-not-allowed flex items-center gap-2" : "bg-green-500 text-white hover:bg-green-600"}`}
                >
                  {isSubmitting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                      {t("Submitting") || "Submitting..."}
                    </>
                  ) : (
                    t("Submit") || "Submit"
                  )}
                </button>
              )}
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdminObservationEvaluationModal;
